'use client';

import { useState, useEffect, useLayoutEffect, useMemo, useCallback } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { ChevronUp, ChevronDown, ArrowUpDown } from 'lucide-react';

/**
 * =============================================================================
 * DataTable Component
 * =============================================================================
 *
 * A generic, reusable data table component with the following features:
 * - Responsive column hiding based on container width
 * - Mobile card view below 900px (when cardRenderer is provided)
 * - Sortable columns with customizable sort behavior
 * - Sticky headers
 * - Row click handling
 *
 * =============================================================================
 * SORTING FEATURE
 * =============================================================================
 *
 * To enable sorting on a table:
 *
 * 1. Add `sortable` prop to DataTable:
 *    <DataTable data={users} columns={columns} sortable />
 *
 * 2. Columns are sortable by default when table has `sortable` prop.
 *    To disable sorting on specific columns (e.g., action columns):
 *    { key: 'actions', label: 'Actions', sortable: false }
 *
 * 3. For columns with non-standard values (dates, custom objects, etc.),
 *    provide a custom sort function:
 *    {
 *      key: 'lastLogin',
 *      label: 'Last Active',
 *      sortFn: (a, b, direction) => {
 *        // Return negative if a should come before b
 *        // Return positive if a should come after b
 *        // Return 0 if equal
 *        // The 'direction' param tells you if sorting asc or desc
 *        if (!a.lastLogin) return 1;  // nulls last
 *        if (!b.lastLogin) return -1;
 *        const diff = new Date(a.lastLogin).getTime() - new Date(b.lastLogin).getTime();
 *        return direction === 'asc' ? diff : -diff;
 *      }
 *    }
 *
 * 4. To use a different key for sorting than display:
 *    { key: 'displayName', label: 'Name', sortKey: 'lastName' }
 *
 * 5. To set default sort direction when first clicking a column:
 *    { key: 'points', label: 'Points', defaultSortDirection: 'desc' }
 *    (Default: 'asc' for strings, 'desc' for numbers)
 *
 * 6. For controlled sorting (e.g., URL persistence):
 *    <DataTable
 *      sortable
 *      sort={{ column: 'name', direction: 'asc' }}
 *      onSortChange={(sort) => updateURL(sort)}
 *    />
 *
 * SORT CYCLE BEHAVIOR:
 * - First click: Sort by that column (desc for numbers, asc for text)
 * - Second click: Reverse direction
 * - Third click: Reset to default sort (or no sort if no default)
 *
 * =============================================================================
 */

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

/** Sort direction */
export type SortDirection = 'asc' | 'desc';

/** Sort state */
export interface SortState<T> {
  column: keyof T;
  direction: SortDirection;
}

/**
 * Column definition for DataTable
 *
 * @template T - The type of data items in the table
 */
export interface Column<T> {
  /** The key in the data object to display */
  key: keyof T;

  /** Column header label */
  label: string;

  /**
   * Optional node rendered immediately after the header label, e.g. a help
   * tooltip explaining what the column means.
   *
   * Kept OUTSIDE the sortable click target: an interactive adornment inside it
   * would sort the table on its way to opening. The header's accessible name
   * stays `label` either way (see `getAriaLabel`), so the adornment must carry
   * its own text for screen readers.
   */
  labelAdornment?: React.ReactNode;

  /** Custom render function for cell content */
  render?: (value: any, item: T) => React.ReactNode;

  /** Fixed width for the column (e.g., '150px') */
  width?: string;

  /** Hide column below this container width (e.g., 1024 hides when container < 1024px) */
  hideBelow?: number;

  /**
   * Whether this column is sortable.
   * Default: true if table has `sortable` prop, false otherwise.
   * Set to false explicitly for action columns or non-sortable data.
   */
  sortable?: boolean;

  /**
   * Custom sort function for non-standard values (dates, nested objects, etc.)
   *
   * @param a - First item to compare
   * @param b - Second item to compare
   * @param direction - Current sort direction ('asc' or 'desc')
   * @returns Negative if a < b, positive if a > b, 0 if equal
   *
   * @example
   * // Sort dates with nulls last
   * sortFn: (a, b, dir) => {
   *   if (!a.date) return 1;
   *   if (!b.date) return -1;
   *   const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
   *   return dir === 'asc' ? diff : -diff;
   * }
   */
  sortFn?: (a: T, b: T, direction: SortDirection) => number;

  /**
   * Use a different key for sorting than the display key.
   * Useful when display value differs from sort value.
   *
   * @example
   * { key: 'fullName', sortKey: 'lastName' }
   */
  sortKey?: keyof T;

  /**
   * Default sort direction when first clicking this column.
   * Default: 'asc' for strings, 'desc' for numbers.
   */
  defaultSortDirection?: SortDirection;

  /**
   * Text alignment for the column.
   * Default: 'left'
   */
  align?: 'left' | 'center' | 'right';

  /** Truncate overflowing text with ellipsis. Useful for name columns in table-fixed layouts. */
  truncate?: boolean;

  /**
   * Render cells in this column as `<th scope="row">` instead of `<td>`.
   * Use for columns that identify each row (e.g., first/last name, roster name)
   * so screen readers announce the row header when navigating other cells (WCAG 1.3.1).
   */
  rowHeader?: boolean;
}

/**
 * Props for DataTable component
 *
 * @template T - The type of data items in the table
 */
interface DataTableProps<T> {
  /** Array of data items to display */
  data: T[];

  /** Column definitions */
  columns: Column<T>[];

  /** Show loading state */
  isLoading?: boolean;

  /** Error message to display */
  error?: string | null;

  /** Message shown when data array is empty */
  emptyMessage?: React.ReactNode;

  /** Use compact row padding */
  compact?: boolean;

  /** Minimum row height in pixels (ensures consistent row heights) */
  minRowHeight?: number;

  /**
   * Window-virtualize rows when the dataset is large. Only the rows near the
   * viewport are mounted, so very large rosters (1k+) no longer pause on paint
   * or freeze when a row mounts heavy per-row UI (e.g. the Access lens cell).
   * The table still scrolls with the page and the sticky header is preserved.
   *
   * Virtualization requires fixed-height rows, so `minRowHeight` (or the 56px
   * default) is treated as the authoritative row height. Activates only past
   * `virtualizeThreshold` rows — below it the normal full-render path is used
   * so small tables keep identical DOM.
   *
   * A11y tradeoff: off-window rows are not in the DOM, so keyboard/AT users
   * reach a row's controls only after scrolling it into view. `aria-rowcount`
   * + per-row `aria-rowindex` communicate the true total set size, and the
   * content stays reachable via keyboard-operable page scrolling.
   */
  virtualized?: boolean;

  /**
   * Row count above which `virtualized` actually kicks in. Default 60. Small
   * datasets render every row (no windowing overhead, stable DOM for tests).
   */
  virtualizeThreshold?: number;

  /** Custom card renderer for mobile view (used when container < 900px) */
  cardRenderer?: (item: T, index: number, containerWidth: number, onItemClick?: () => void) => React.ReactNode;

  /** Add gap between cards in card view (default: true) */
  cardGap?: boolean;

  /** Callback when a row/card is clicked */
  onRowClick?: (item: T) => void;

  /** Make table header sticky at this offset from top (e.g., height of toolbar above) */
  stickyHeaderTop?: number;

  // -------------------------------------------------------------------------
  // Sorting props
  // -------------------------------------------------------------------------

  /**
   * Enable sorting on this table.
   * When true, columns are sortable by default (unless column.sortable = false).
   */
  sortable?: boolean;

  /**
   * Default sort state (uncontrolled mode).
   * Used as initial sort and as reset target after 3rd click.
   */
  defaultSort?: SortState<T>;

  /**
   * Remove outer border and rounded corners.
   * Use when embedding the table in a container that already has borders.
   */
  noBorder?: boolean;

  /**
   * Square off the top-left/top-right corners of the table card. Use when
   * something else (e.g. a colored banner) sits flush against the top of
   * the table and the table's rounded top would otherwise peek out.
   */
  flatTop?: boolean;

  /**
   * Accessible label for the table element.
   * Applied as aria-label on the <table> for screen readers (WCAG 1.3.1).
   */
  ariaLabel?: string;

  /**
   * Visually hidden caption for the table. Screen readers announce this
   * when the user enters the table. Use a short phrase describing the
   * table contents, e.g. "Rosters".
   */
  caption?: string;

  /**
   * Build an accessible name for a clickable row. Required in practice
   * whenever `onRowClick` is set — without it screen readers announce
   * each row as just "row".
   */
  getRowAriaLabel?: (item: T) => string;

  /**
   * Per-row className hook. Return extra classes to merge onto the row's
   * <tr>. Useful for transient row-level effects (e.g. flashing the most
   * recently inserted row). Returning undefined leaves the row untouched.
   */
  getRowClassName?: (item: T, index: number) => string | undefined;

  /**
   * `data-tour` value stamped on EVERY row's <tr>.
   *
   * For a guided walk whose step is "pick one of these". The tour engine
   * advances a click step by matching `closest(target)` on the click, so the
   * anchor has to be an element the click lands inside — and a click lands
   * anywhere in a row, not on the one cell a `render` could tag. Every row
   * carries the same value: the spotlight resolves to the first, and any of
   * them satisfies the click.
   */
  rowDataTour?: string;

  /**
   * Controlled sort state.
   * When provided, component is in controlled mode - you must handle onSortChange.
   */
  sort?: SortState<T> | null;

  /**
   * Callback when sort changes (controlled mode).
   * Called with new sort state, or null when sort is cleared.
   */
  onSortChange?: (sort: SortState<T> | null) => void;
}

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------

// Breakpoint for card view with hysteresis to prevent flashing
const CARD_VIEW_BREAKPOINT = 900;
const CARD_VIEW_HYSTERESIS = 50; // Buffer zone to prevent oscillation

// Default fixed row height (px) used by virtualized table rows when no
// explicit `minRowHeight` is provided. Rows are laid out at a fixed height,
// so a constant estimate is exact.
const DEFAULT_VIRTUAL_ROW_HEIGHT = 56;

// Initial size estimate (px) for virtualized cards. Card heights vary, so this
// is only a seed — actual heights are measured via `measureElement`.
const VIRTUAL_CARD_ESTIMATE = 160;

// Extra rows rendered above/below the viewport so fast scrolls and the sticky
// header overlap never reveal blank space.
const VIRTUAL_OVERSCAN = 8;

/**
 * Narrowest a width-less ("flex") column may get before its own header stops
 * fitting (ENG-9722).
 *
 * `table-fixed` hands a width-less column whatever is left after the fixed
 * ones, so the caller's responsive thresholds are what decide how much that is.
 * A two-word sortable header ("Last Name") renders a 106px pill inside a cell
 * with 48px of horizontal padding, so anything under ~154px wraps the label
 * onto two lines; well under it, the pill stops fitting at all. Rounded up for
 * headroom on labels longer than "Last Name".
 *
 * Callers computing "can I still afford to show the optional column?" should
 * budget `sum(fixedWidths) + MIN_FLEX_COLUMN_PX`.
 */
export const MIN_FLEX_COLUMN_PX = 160;

// Cache last measured width so re-mounts (e.g. navigating back) don't jitter
let lastKnownWidth = 1200;

// -----------------------------------------------------------------------------
// Helper Functions
// -----------------------------------------------------------------------------

/**
 * Detect if a value is likely a number for default sort direction.
 * Numbers default to descending (high to low), strings to ascending (A-Z).
 */
function isNumericValue(value: any): boolean {
  return typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)) && value.trim() !== '');
}

/**
 * Default comparison function for sorting.
 * Handles strings, numbers, and null/undefined values.
 * Nulls are always sorted last regardless of direction.
 */
function defaultCompare<T>(
  a: T,
  b: T,
  key: keyof T,
  direction: SortDirection
): number {
  const aVal = a[key];
  const bVal = b[key];

  // Handle null/undefined - always sort to end
  if (aVal == null && bVal == null) return 0;
  if (aVal == null) return 1;
  if (bVal == null) return -1;

  // Compare based on type
  let comparison: number;

  if (typeof aVal === 'number' && typeof bVal === 'number') {
    comparison = aVal - bVal;
  } else {
    // Convert to strings for comparison
    const aStr = String(aVal).toLowerCase();
    const bStr = String(bVal).toLowerCase();
    comparison = aStr.localeCompare(bStr);
  }

  // Apply direction
  return direction === 'asc' ? comparison : -comparison;
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function DataTable<T extends Record<string, any>>({
  data,
  columns,
  isLoading = false,
  error = null,
  emptyMessage = 'No data available',
  compact = false,
  minRowHeight,
  virtualized = false,
  virtualizeThreshold = 60,
  cardRenderer,
  cardGap = true,
  onRowClick,
  stickyHeaderTop,
  // Sorting props
  sortable = false,
  defaultSort,
  sort: controlledSort,
  onSortChange,
  noBorder = false,
  flatTop = false,
  ariaLabel,
  caption,
  getRowAriaLabel,
  rowDataTour,
  getRowClassName,
}: DataTableProps<T>) {
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(() => lastKnownWidth);
  const [isCardView, setIsCardView] = useState(() => lastKnownWidth < CARD_VIEW_BREAKPOINT);

  // Callback ref: triggers ResizeObserver setup whenever the container element appears/changes
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    setContainerEl(node);
  }, []);

  // -------------------------------------------------------------------------
  // Sort State Management
  // -------------------------------------------------------------------------

  // Internal sort state (used in uncontrolled mode)
  const [internalSort, setInternalSort] = useState<SortState<T> | null>(defaultSort || null);

  // Determine if we're in controlled mode
  const isControlled = controlledSort !== undefined;

  // Current sort state (controlled or uncontrolled)
  const currentSort = isControlled ? controlledSort : internalSort;

  /**
   * Handle sort column click.
   * Implements the sort cycle:
   * - First click: Sort by column (desc for numbers, asc for text)
   * - Second click: Reverse direction
   * - Third click: Reset to default sort
   */
  const handleSort = useCallback((column: Column<T>) => {
    const columnKey = column.sortKey || column.key;

    // Determine default direction for this column
    const getDefaultDirection = (): SortDirection => {
      if (column.defaultSortDirection) return column.defaultSortDirection;
      // Check first non-null value to determine if numeric
      const sampleValue = data.find(item => item[columnKey] != null)?.[columnKey];
      return isNumericValue(sampleValue) ? 'desc' : 'asc';
    };

    let newSort: SortState<T> | null;

    if (currentSort?.column === columnKey) {
      // Already sorting by this column - cycle through states
      if (currentSort.direction === getDefaultDirection()) {
        // First click was default direction, now reverse
        newSort = {
          column: columnKey,
          direction: currentSort.direction === 'asc' ? 'desc' : 'asc',
        };
      } else {
        // Was reversed, now reset to default sort (or null if no default)
        newSort = defaultSort || null;
      }
    } else {
      // New column - start with default direction for this column
      newSort = {
        column: columnKey,
        direction: getDefaultDirection(),
      };
    }

    // Update state (controlled or uncontrolled)
    if (isControlled) {
      onSortChange?.(newSort);
    } else {
      setInternalSort(newSort);
      // Also notify listener in uncontrolled mode (for analytics, etc.)
      onSortChange?.(newSort);
    }
  }, [currentSort, defaultSort, isControlled, onSortChange, data]);

  // -------------------------------------------------------------------------
  // Sorted Data
  // -------------------------------------------------------------------------

  const sortedData = useMemo(() => {
    if (!sortable || !currentSort) return data;

    const { column: sortColumn, direction } = currentSort;

    // Find column definition to get custom sort function
    const columnDef = columns.find(
      col => (col.sortKey || col.key) === sortColumn
    );

    return [...data].sort((a, b) => {
      if (columnDef?.sortFn) {
        // Use custom sort function
        return columnDef.sortFn(a, b, direction);
      }
      // Use default comparison
      return defaultCompare(a, b, sortColumn, direction);
    });
  }, [data, sortable, currentSort, columns]);

  // -------------------------------------------------------------------------
  // Container Width Tracking (for responsive features)
  // -------------------------------------------------------------------------

  // Measure initial width synchronously before paint to avoid column jitter
  useLayoutEffect(() => {
    if (!containerEl) return;
    const initialWidth = containerEl.offsetWidth;
    if (initialWidth > 0) {
      lastKnownWidth = initialWidth;
      setContainerWidth(initialWidth);
      setIsCardView(initialWidth < CARD_VIEW_BREAKPOINT);
    }
  }, [containerEl]);

  useEffect(() => {
    if (!containerEl) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const newWidth = Math.round(entry.contentRect.width);
        lastKnownWidth = newWidth;
        setContainerWidth(newWidth);

        // Apply hysteresis to prevent oscillation at breakpoint
        setIsCardView((wasCardView) => {
          if (wasCardView) {
            // Currently in card view - only switch to table if width exceeds breakpoint + buffer
            return newWidth < CARD_VIEW_BREAKPOINT + CARD_VIEW_HYSTERESIS;
          } else {
            // Currently in table view - only switch to card if width drops below breakpoint
            return newWidth < CARD_VIEW_BREAKPOINT;
          }
        });
      }
    });

    resizeObserver.observe(containerEl);
    return () => resizeObserver.disconnect();
  }, [containerEl]);

  // Filter columns based on container width
  const visibleColumns = columns.filter(
    (col) => !col.hideBelow || containerWidth >= col.hideBelow
  );

  // -------------------------------------------------------------------------
  // Row Virtualization (large datasets)
  // -------------------------------------------------------------------------

  // Authoritative fixed row height for virtualized table rows.
  const virtualRowHeight = minRowHeight ?? DEFAULT_VIRTUAL_ROW_HEIGHT;

  // Only window large datasets; small ones render every row (stable DOM, no overhead).
  const isVirtual = virtualized && sortedData.length > virtualizeThreshold;

  // The element holding the virtualized rows/cards. The page (window) is the
  // scroller, so the list's document-relative top becomes the virtualizer's
  // `scrollMargin` — that maps window scroll onto the right item even though
  // the page header + toolbar sit above the list.
  const [listEl, setListEl] = useState<HTMLElement | null>(null);
  const listElRef = useCallback((node: HTMLElement | null) => setListEl(node), []);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (!isVirtual || !listEl) return;
    const measure = () => {
      const top = listEl.getBoundingClientRect().top + window.scrollY;
      // Guard against feedback loops: only re-render when it actually moves.
      setScrollMargin((prev) => (Math.abs(prev - top) > 1 ? top : prev));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isVirtual, listEl, isCardView, sortedData.length, stickyHeaderTop]);

  const virtualizer = useWindowVirtualizer({
    // `enabled: false` makes the virtualizer fully inert: it attaches no window
    // scroll/resize listeners and does no work. Critical so the many
    // non-virtualized DataTable consumers don't pay for listeners + scroll-time
    // re-renders they never asked for.
    enabled: isVirtual,
    count: sortedData.length,
    estimateSize: () => (isCardView ? VIRTUAL_CARD_ESTIMATE : virtualRowHeight),
    overscan: VIRTUAL_OVERSCAN,
    scrollMargin,
  });

  // estimateSize changes basis (table row vs card) across the 900px breakpoint,
  // but the virtualizer's per-index size cache survives the re-render. Clear it
  // on the switch so stale measurements don't corrupt total height / spacers.
  useEffect(() => {
    if (isVirtual) virtualizer.measure();
  }, [isCardView]);

  // Coordinates are in the window scroll space (start/end already include
  // scrollMargin); subtract it back out to get list-relative spacer heights.
  const virtualItems = isVirtual ? virtualizer.getVirtualItems() : [];
  const virtualTotalSize = isVirtual ? virtualizer.getTotalSize() : 0;
  const virtualPaddingTop =
    virtualItems.length > 0 ? virtualItems[0].start - scrollMargin : 0;
  const virtualPaddingBottom =
    virtualItems.length > 0
      ? virtualTotalSize - (virtualItems[virtualItems.length - 1].end - scrollMargin)
      : 0;

  // -------------------------------------------------------------------------
  // Render Helpers
  // -------------------------------------------------------------------------

  /**
   * Get tooltip text for a sortable column header
   */
  const getSortTooltip = (column: Column<T>): string | undefined => {
    if (!sortable) return undefined;
    if (column.sortable === false) return undefined;

    const columnKey = column.sortKey || column.key;
    const isSorted = currentSort?.column === columnKey;

    if (!isSorted) return 'Click to sort';
    if (currentSort?.direction === 'desc') return 'Click to sort ascending';
    return 'Click to reset sort';
  };

  /**
   * Get ARIA label for a sortable column header.
   * Provides screen reader context about current sort state and available action.
   */
  const getAriaLabel = (column: Column<T>): string => {
    const columnKey = column.sortKey || column.key;
    const isSortable = sortable && column.sortable !== false;
    const isSorted = currentSort?.column === columnKey;

    if (!isSortable) {
      return column.label;
    }

    if (!isSorted) {
      return `${column.label}, click to sort`;
    }

    const currentDirection = currentSort?.direction === 'asc' ? 'ascending' : 'descending';
    const nextAction = currentSort?.direction === 'desc' ? 'sort ascending' : 'reset sort';
    return `${column.label}, sorted ${currentDirection}, click to ${nextAction}`;
  };

  /**
   * Render sort icon for a column header
   */
  const renderSortIcon = (column: Column<T>) => {
    if (!sortable) return null;
    if (column.sortable === false) return null;

    const columnKey = column.sortKey || column.key;
    const isSorted = currentSort?.column === columnKey;

    // Choose icon based on sort state
    const Icon = isSorted
      ? (currentSort?.direction === 'asc' ? ChevronUp : ChevronDown)
      : ArrowUpDown;

    return (
      <Icon
        className={`w-3.5 h-3.5 transition-colors ${
          isSorted ? 'opacity-100 text-primary-600' : 'opacity-30'
        }`}
      />
    );
  };

  // -------------------------------------------------------------------------
  // Loading State
  // -------------------------------------------------------------------------

  if (isLoading) {
    return (
      <div
        ref={containerRef}
        className="p-8 text-center text-gray-500"
        role="status"
        aria-live="polite"
        aria-label="Loading data"
      >
        <div className="inline-block animate-spin" aria-hidden="true">⚙️</div> Loading...
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Error State
  // -------------------------------------------------------------------------

  if (error) {
    return (
      <div ref={containerRef} className="p-8 bg-red-50 border border-red-200 rounded-lg">
        <p className="text-red-800 font-medium">Error loading data</p>
        <p className="text-red-600 text-sm mt-1">{error}</p>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Empty State
  // -------------------------------------------------------------------------

  if (data.length === 0) {
    return (
      <div ref={containerRef} className={`p-8 text-center text-gray-500 bg-white ${noBorder ? '' : 'rounded-lg border border-gray-200'}`}>
        {emptyMessage}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Card View (Mobile)
  // -------------------------------------------------------------------------

  // Mobile card view when below breakpoint and cardRenderer is provided
  const showCardView = cardRenderer && isCardView;

  if (showCardView) {
    if (isVirtual) {
      return (
        <div ref={containerRef}>
          <div
            ref={listElRef}
            style={{ height: virtualTotalSize, position: 'relative', width: '100%' }}
          >
            {virtualItems.map((vItem) => {
              const item = sortedData[vItem.index];
              return (
                <div
                  key={vItem.key}
                  data-index={vItem.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${vItem.start - scrollMargin}px)`,
                    // Replaces the `space-y-3` gap (which can't apply to
                    // absolutely-positioned items); measured as row height.
                    paddingBottom: cardGap ? 12 : 0,
                  }}
                >
                  {cardRenderer(item, vItem.index, containerWidth, onRowClick ? () => onRowClick(item) : undefined)}
                </div>
              );
            })}
          </div>
        </div>
      );
    }
    return (
      <div ref={containerRef} className={cardGap ? 'space-y-3' : ''}>
        {sortedData.map((item, index) => (
          <div key={index}>
            {cardRenderer(item, index, containerWidth, onRowClick ? () => onRowClick(item) : undefined)}
          </div>
        ))}
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Table View (Desktop)
  // -------------------------------------------------------------------------

  // Render one data row. Shared by the full-render and virtualized table
  // paths so row markup/behavior stays identical between them. In virtualized
  // mode the <tr> is tagged for the virtualizer to measure its real height.
  const renderRow = (item: T, index: number) => {
    const isLastRow = !noBorder && index === sortedData.length - 1;
    // Derive an accessible name for clickable rows. Prefer the caller's
    // getRowAriaLabel; otherwise fall back to the rowHeader column's
    // raw value so screen readers never announce a bare "button".
    let rowAriaLabel: string | undefined;
    if (onRowClick) {
      if (getRowAriaLabel) {
        rowAriaLabel = getRowAriaLabel(item);
      } else {
        const rowHeaderColumn = visibleColumns.find((c) => c.rowHeader);
        if (rowHeaderColumn) {
          const raw = item[rowHeaderColumn.key];
          if (typeof raw === 'string' || typeof raw === 'number') {
            rowAriaLabel = String(raw);
          }
        }
      }
    }
    // Row-level click handler. The <tr> no longer carries role,
    // tabindex, or aria-label — doing so nested interactive controls
    // (tr-as-button + inner action buttons like "Download") and
    // tripped axe's "nested-interactive" rule. Instead, keyboard
    // activation lives on a <button> inside the row-header cell.
    const rowHeaderCol = visibleColumns.find((c) => c.rowHeader);
    const rowActivate = onRowClick ? () => onRowClick(item) : undefined;
    const extraRowClass = getRowClassName?.(item, index);
    const rowClassName = [
      onRowClick ? 'hover:bg-gray-50 cursor-pointer' : '',
      extraRowClass ?? '',
    ].filter(Boolean).join(' ');
    return (
      <tr
        key={index}
        ref={isVirtual ? virtualizer.measureElement : undefined}
        data-tour={rowDataTour}
        data-index={isVirtual ? index : undefined}
        aria-rowindex={isVirtual ? index + 2 : undefined}
        className={rowClassName}
        onClick={onRowClick ? (e) => {
          // Skip clicks on interactive children — action buttons, links, form controls,
          // and the row-header button keep their own behavior. Without this guard
          // we'd double-fire (button click + row click) and break action menus.
          if ((e.target as HTMLElement).closest(
            'button, a, input, select, textarea, label, [role="button"], [role="menuitem"], [role="checkbox"]'
          )) return;
          // Don't activate when the user is dragging to select text in the row.
          if (window.getSelection()?.toString()) return;
          onRowClick(item);
        } : undefined}
      >
        {visibleColumns.map((column, colIndex) => {
          const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';
          const radiusClass = isLastRow
            ? colIndex === 0
              ? 'rounded-bl-lg'
              : colIndex === visibleColumns.length - 1
                ? 'rounded-br-lg'
                : ''
            : '';
          // Keep className identical between <th scope="row"> and <td> so visual layout is unchanged.
          // `font-normal` prevents browser default bold styling on <th>.
          const cellClassName = `px-6 ${alignClass} text-sm text-gray-700 ${compact ? 'py-2' : 'py-4'} ${radiusClass}${column.truncate ? ' truncate' : ''}${column.rowHeader ? ' font-normal' : ''}`;
          const cellStyle = minRowHeight ? { height: minRowHeight } : undefined;
          const cellContent = column.render
            ? column.render(item[column.key], item)
            : item[column.key];

          if (column.rowHeader) {
            const isPrimaryHeader = rowHeaderCol && column.key === rowHeaderCol.key;
            const content = isPrimaryHeader && rowActivate ? (
              <button
                type="button"
                onClick={() => rowActivate()}
                aria-label={rowAriaLabel}
                className={`text-left w-full font-normal text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 rounded-sm${column.truncate ? ' block truncate' : ''}`}
              >
                {cellContent}
              </button>
            ) : cellContent;
            return (
              <th
                key={`${String(column.key)}-${colIndex}`}
                scope="row"
                className={cellClassName}
                style={cellStyle}
              >
                {content}
              </th>
            );
          }

          return (
            <td
              key={`${String(column.key)}-${colIndex}`}
              className={cellClassName}
              style={cellStyle}
            >
              {cellContent}
            </td>
          );
        })}
      </tr>
    );
  };

  return (
    <div
      ref={containerRef}
      className={
        noBorder
          ? 'bg-white'
          : `border border-gray-200 bg-white ${flatTop ? 'rounded-b-lg' : 'rounded-lg'}`
      }
      style={
        noBorder
          ? undefined
          : {
              clipPath: flatTop
                ? 'inset(0 round 0 0 0.5rem 0.5rem)'
                : 'inset(0 round 0.5rem)',
            }
      }
    >
      <table
        className="w-full table-fixed"
        aria-label={ariaLabel}
        // When virtualized, only a window of rows is in the DOM. Expose the
        // true total so assistive tech reports the full roster size, and pair
        // it with aria-rowindex on each row (header = 1, data rows = index+2).
        aria-rowcount={isVirtual ? sortedData.length + 1 : undefined}
      >
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead
          className={`bg-gray-50 ${stickyHeaderTop !== undefined ? 'sticky z-20 shadow-[0_1px_0_0_#e5e7eb]' : 'border-b border-gray-200'}`}
          style={stickyHeaderTop !== undefined ? { top: stickyHeaderTop } : undefined}
        >
          <tr aria-rowindex={isVirtual ? 1 : undefined}>
            {visibleColumns.map((column, index) => {
              const columnKey = column.sortKey || column.key;
              const isSortable = sortable && column.sortable !== false;
              const isSorted = currentSort?.column === columnKey;

              const alignClass = column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left';

              return (
                <th
                  key={`${String(column.key)}-${index}`}
                  scope="col"
                  onClick={isSortable ? () => handleSort(column) : undefined}
                  onKeyDown={isSortable ? (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSort(column);
                    }
                  } : undefined}
                  tabIndex={isSortable ? 0 : undefined}
                  role="columnheader"
                  title={getSortTooltip(column)}
                  aria-label={getAriaLabel(column)}
                  aria-sort={
                    isSorted
                      ? currentSort?.direction === 'asc'
                        ? 'ascending'
                        : 'descending'
                      : isSortable
                        ? 'none'
                        : undefined
                  }
                  className={`px-6 py-3 ${alignClass} text-sm font-semibold text-gray-900 bg-gray-50 ${
                    !noBorder && !flatTop && index === 0 ? 'rounded-tl-lg' : ''
                  } ${!noBorder && !flatTop && index === visibleColumns.length - 1 ? 'rounded-tr-lg' : ''} ${
                    isSortable ? 'cursor-pointer select-none' : ''
                  }`}
                  style={column.width ? { width: column.width } : undefined}
                >
                  {/*
                    Sortable header content wrapper.
                    Uses inner div for hover/active styling so it doesn't affect full cell.
                  */}
                  {isSortable ? (
                    // Bounded width + a truncating label (ENG-9722). An
                    // inline-flex box is sized purely by its content, so on a
                    // squeezed column it kept its intrinsic width and painted
                    // the label — and its highlight — straight over the next
                    // header. A cap makes the label absorb the shortfall by
                    // ellipsizing instead. Still `inline-flex`, so on a normal
                    // column the highlight hugs the label rather than filling
                    // the cell.
                    //
                    // The cap is the cell's FULL width — content box + this
                    // `th`'s own `px-6` (3rem) — not `max-w-full`, which is the
                    // content box alone. Narrow fixed columns depend on that
                    // padding: `Source` at 92px has only 44px of content box,
                    // so `max-w-full` truncated a header that has always
                    // rendered in full to a bare "S". Overhanging into a cell's
                    // own padding is fine; the next column's text is still
                    // 24px clear of it, which is the boundary that matters.
                    <div
                      className={`inline-flex max-w-[calc(100%+3rem)] min-w-0 items-center gap-1.5 px-2 py-1 -mx-2 -my-1 rounded-md transition-colors ${
                        isSorted
                          ? 'bg-blue-100 text-blue-900'
                          : 'hover:bg-blue-100 hover:text-blue-900'
                      }`}
                    >
                      <span className="truncate">{column.label}</span>
                      {/* The sort direction is the one thing that must survive
                          the clip — it's the only cue that this column is the
                          one being sorted. */}
                      <span className="flex-shrink-0 inline-flex">{renderSortIcon(column)}</span>
                    </div>
                  ) : (
                    column.label
                  )}
                  {column.labelAdornment}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody ref={isVirtual ? listElRef : undefined} className="divide-y divide-gray-200">
          {isVirtual ? (
            <>
              {virtualPaddingTop > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={visibleColumns.length} style={{ height: virtualPaddingTop, padding: 0, border: 0 }} />
                </tr>
              )}
              {virtualItems.map((vItem) => renderRow(sortedData[vItem.index], vItem.index))}
              {virtualPaddingBottom > 0 && (
                <tr aria-hidden="true">
                  <td colSpan={visibleColumns.length} style={{ height: virtualPaddingBottom, padding: 0, border: 0 }} />
                </tr>
              )}
            </>
          ) : (
            sortedData.map((item, index) => renderRow(item, index))
          )}
        </tbody>
      </table>
    </div>
  );
}
